import {
  CarrotPlanMediaCategory,
  CarrotPlanMediaItem,
  CarrotPlanMediaType,
  CarrotPlanMenuItemCategory,
  DisplayStatus,
  OptionMetadata
} from "types/CarrotPlanOptions"
import { Employee } from "types/Employee"
import ResponseError from "utils/ResponseError"
import { useCarrotPlanOptions } from "./useCarrotPlanOptions"

export enum FormResponsiveDisplayStatus {
  DUPLICATE_ON_SAME_PLAN = "DUPLICATE_ON_SAME_PLAN"
}

export interface CarrotPlanFormOptionMetadata extends Omit<OptionMetadata, "displayStatus"> {
  displayStatus: DisplayStatus | FormResponsiveDisplayStatus
}

export type MenuItemOptionFields = {
  defaultUrl: string
  entityId: string
}

export type TopicOptionFields = {
  navTitle?: string | undefined
  titleWithCategory?: string | undefined
  options: CarrotPlanMediaItem[]
  link?: string
  entityId: string
}

export type MediaOptionFields = {
  categoryTitle: string
  contentType: CarrotPlanMediaType
  slug?: string
  link?: string
  entryId: string
}

export type CarrotPlanFormSelectOption = {
  group?: string
  label: string
  value: string
  optionMetadata: CarrotPlanFormOptionMetadata
}

export interface CarrotPlanFormOption<TOptionFields> extends CarrotPlanFormSelectOption {
  optionFields: TOptionFields
}

type UseCarrotPlanMappings = {
  mappedFormMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  mappedFormTopics: CarrotPlanFormOption<TopicOptionFields>[]
  areMappingsLoading: boolean
  fetchErrors: ResponseError
}

const mapMenuItemOptionsForAutocomplete = (
  menuOptions: CarrotPlanMenuItemCategory[]
): CarrotPlanFormOption<MenuItemOptionFields>[] => {
  // Flatten the menu options so that all menu items are in a single array
  const mappedMenuItems = menuOptions?.flatMap(({ menuItems, category }) => {
    // Map over the menu items to match the necessary shape for Carotene's FormAutoComplete component
    const mappedItems = menuItems.map(({ displayText, entityId, optionMetadata, defaultUrl, ...rest }) => {
      return {
        label: displayText,
        value: entityId,
        group: category,
        optionMetadata,
        optionFields: {
          ...rest,
          entityId,
          defaultUrl
        }
      }
    })

    // Filter out any menu items that are archived, removed, or unassigned
    return mappedItems.filter(
      (item) =>
        ![DisplayStatus.ARCHIVED, DisplayStatus.REMOVE, DisplayStatus.UNASSIGNED].includes(
          item.optionMetadata.displayStatus
        )
    )
  })

  return mappedMenuItems
}

const mapSubcategoriesForAutocomplete = (
  mediaOptions: CarrotPlanMediaCategory[]
): CarrotPlanFormOption<TopicOptionFields>[] => {
  // Flatten the media options so that all media items are in a single array
  const mappedTopics = mediaOptions?.flatMap((mediaCategory) => {
    const mappedSubCategories = mediaCategory.subCategories.map(
      // Map over the subcategories to match the necessary shape for Carotene's FormAutoComplete component
      ({ categoryTitle, entityId, mediaItems, title, titleWithCategory, navTitle, optionMetadata, ...rest }) => {
        return {
          ...rest,
          label: title,
          group: categoryTitle,
          value: entityId,
          optionMetadata,
          optionFields: {
            navTitle: navTitle,
            titleWithCategory: titleWithCategory,
            options: mediaItems,
            entityId
          }
        }
      }
    )
    // Filter out any subcategories that don't have any media items to show
    return mappedSubCategories.filter((item) => item.optionMetadata.displayStatus === DisplayStatus.SHOW.toString())
  })

  return mappedTopics
}

export const useCarrotPlanMappings = (
  employee: Employee,
  journey?: number,
  journeyStage?: number
): UseCarrotPlanMappings => {
  const { data: carrotPlanOptions, isLoading: areCarrotPlanOptionsLoading, error } = useCarrotPlanOptions(
    employee.employeeId,
    journey,
    journeyStage
  )

  const mappedMenuItems = !areCarrotPlanOptionsLoading
    ? mapMenuItemOptionsForAutocomplete(carrotPlanOptions?.menuOptions)
    : []
  const mappedMediaTopics = !areCarrotPlanOptionsLoading
    ? mapSubcategoriesForAutocomplete(carrotPlanOptions?.mediaOptions)
    : []

  return {
    mappedFormMenuItems: mappedMenuItems,
    mappedFormTopics: mappedMediaTopics,
    areMappingsLoading: areCarrotPlanOptionsLoading,
    fetchErrors: error
  }
}
